import { useParams } from 'react-router-dom';
import { Box, Card, CardHeader, Typography, Stack, Backdrop, CircularProgress } from '@mui/material';
import { Datagrid, ExpandRowButton } from '@/components/list/datagrid';
import { TextField as RaTextField, FunctionField } from '@/components/field';
import { ListContextProvider, ShowBase, ReduxState, RecordMap, crudGetList, ListControllerProps, Identifier, toggleListItemExpand } from 'ra-core';
import { useDispatch, useSelector } from 'react-redux';
import { decodeHashid, genUrlWithHashid } from '@/utils/hashid';
import { Content } from '@/components/content/Content';
import RowExpandFieldSemanticEdit from '@/components/governance/RowExpandFieldSemanticEdit';
import { useResourceRegister } from '@/utils/resourceRegister';
import WithShow from '@/controller/WithShow';
import { FC, MouseEvent, useEffect, useRef } from 'react';
import { DatabaseResponseDto, FieldSchemaResponseDto, SecondaryClassificationResponseDto, TableSchemaResponseDto } from '@/gen/api/governance';
import { RecPatch } from '@/dataProvider/tools';
import { dataTypeDisplayInfo } from '@/components/governance/fieldEdit/maskingRuleDataTypesMap';
import { pages, sorts } from '@/utils/paramsWithGetMainList';
import useLocalListController from '@/components/list/useLocalListController';
import { AtopFuncArea, SearchField } from '@/components/list/datagridFunctionWidgets';
import { OdResourceOption, OdResourcePageProps } from '@/types/resource';
import DatabaseTableSemanticInfoCard from '@/components/governance/DatabaseTableSemanticInfoCard';
import { createPortal } from 'react-dom';
import { datagridClasses } from '@/components/list/datagrid/useDatagridStyles';
import { waitMilis } from '@/utils/asyncUtils';


// Resource constants
const dbSemanticRes = 'governance/database-semantic';
const tableSemanticRes = 'governance/table-semantic';
const fieldSemanticRes = 'governance/database-semantic/:dbId/table-fields';
const tableSemanticStatisRes = 'governance/table-semantic-statis';


/**
 * TableFieldsPage displays field-level metadata for a specific table
 * Shows context cards (Database -> Schema -> Table hierarchy) and a Fields datagrid
 */
const TableFieldsPage : FC<OdResourcePageProps>= ({
  options
}) => {
  useResourceRegister({ name: tableSemanticRes })
  useResourceRegister({ name: tableSemanticStatisRes })

  const { id: tableHashId, dbId: dbHashId } = useParams<{ id: string, dbId: string }>();

  // Decode table ID from hashid
  const tableId = decodeHashid(tableHashId || '');
  const databaseId = decodeHashid(dbHashId || '');

  const resReady = useSelector<ReduxState, boolean>(store => {
    const resources = store.admin.resources
    return Boolean(resources[tableSemanticRes])
  })
  if (!resReady) { return null }

  return (
    <ShowBase id={databaseId} resource={dbSemanticRes} basePath="" >
    <WithShow<DatabaseResponseDto&RecPatch> render={({ record: database, }) => (

      <ShowBase id={`${databaseId}-${tableId}`} resource={tableSemanticRes} basePath="" >
      <WithShow<TableSchemaResponseDto&RecPatch> render={({ record: table, }) => (

        <TableFieldsPageBody databaseId={databaseId} tableId={tableId} database={database} table={table} 
          options={options}
        />

      )} />
      </ShowBase>
    )} />
    </ShowBase>
  )
}

const TableFieldsPageBody : FC<{
  databaseId: number;
  tableId: number;
  database?: DatabaseResponseDto;
  table?: TableSchemaResponseDto;
  options?: OdResourceOption, 
}> = ({
  databaseId, tableId, database, table, options,
}) => {
  const dispatch = useDispatch();

  // Fields list controller
  const fieldsListCtrl = useLocalListController({
    resource: fieldSemanticRes, basePath: '',
    filter: { tableId, databaseId },
    perPage: 2000,
  }, { sorters });
  const { loading } = fieldsListCtrl

  const classificationData = useSelector<ReduxState, RecordMap<SecondaryClassificationResponseDto&RecPatch>>(store => store.admin.resources['settings/governance/data-classification']?.data || {})
  useEffect(() => {
    dispatch(crudGetList('settings/governance/data-classification', pages(), sorts(), { useCache: true }))
  }, [dispatch])
  const { setRef, ExpandAllPortal } = useExpandAllHelpers({ fieldsListCtrl })

  return (
    <Content
      resourceName='Edit Semantic Data'
      pageName='Edit Semantic Data'
      breadcrumbs={[
        ...(options?.breadcrumbs ?? []),
        { label: 'Database Semantics', path: `/governance/database-semantic/${genUrlWithHashid("$0", databaseId, database?.name ?? '')}/edit` },
      ]}
    >
      {/* Context Cards - Database -> Schema -> Table Hierarchy */}
      {/* Database Card */}
      <Box sx={{ mb: 3 }}>
        {(database && table) &&
          <DatabaseTableSemanticInfoCard database={database} table={table} />}
      </Box>

      {/* Fields Section */}
      <ListContextProvider value={fieldsListCtrl} >
        <Card>
          <CardHeader 
            title={
              <Stack direction="row" alignItems="center" >
                <Typography variant="h6" component="span">
                  Fields
                </Typography>
                {fieldsListCtrl.total && 
                  <Typography variant="body2" component="span" sx={{ ml: 2, color: theme => theme.palette.secondary.main }}>
                    {fieldsListCtrl.total}
                  </Typography>}

                <AtopFuncArea boxSx={{ p: 0, ml: '54px' }}>
                  <SearchField sourceKey="name" sx={{ width: 280 }}
                    TextFieldProps={{ placeholder: 'Search fields' }}
                  />
                </AtopFuncArea>
              </Stack>
            }
          />
          <Datagrid ref={setRef}
            sx={{ 
              '& th' : { pb: 1 },
              '& td' : { py: 1 },
              '& th.RaDatagrid-expandHeader': { textAlign: 'center' },
              '& td.column-description': { width: 300 },
              '& td.column-comment': { width: 200 },
            }}
            expand={<RowExpandFieldSemanticEdit />}
            rowClick="expand"
            {...fieldsListCtrl}
          >
            <RaTextField label="Field Name" source="name" sortable={false} />
            <FunctionField<FieldSchemaResponseDto&RecPatch> sortable={false}
              label="Data Type"
              render={(d) => {
                if (!d) { return null }
                const info = dataTypeDisplayInfo({...d, dataType: d.type})
                return (
                  <Typography
                      component="span"
                      variant="body2"
                  >
                    {(d.isIdentity || d.isComputed) && <Box>
                      {d.isIdentity && <Typography variant="caption" color="text.secondary">[Identity]</Typography>}
                      {d.isComputed && <Typography variant="caption" color="text.secondary">[Computed]</Typography>}
                      <br />
                    </Box>}
                    {info.dataType}{info.dataTypeExt}
                  </Typography>
                )
              }}
            />
            <FunctionField<FieldSchemaResponseDto&RecPatch> sortable={false}
              label="Classification"
              render={(record) => {
                const clss = classificationData[record?.secondaryClassificationId!]
                return (
                  <Typography variant="caption">
                    {clss?.primaryClassificationText} - {clss?.name}
                  </Typography>
                )
              }}
            />
            <RaTextField label="Description" source="description" className='description' sortable={false} />
            <RaTextField label="Comment" source="comment" className='comment' sortable={false} />
          </Datagrid>
          <ExpandAllPortal />
        </Card>
      </ListContextProvider>
      <Backdrop open={loading} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress />
      </Backdrop>
    </Content>
  );
};

export default TableFieldsPage;


const useExpandAllHelpers = ({
  fieldsListCtrl,
}: {
  fieldsListCtrl: ListControllerProps<any>,
}) => {
  const th_ref = useRef<HTMLElement|null>(null)
  const setRef = (table: HTMLTableElement) => {
    th_ref.current = table?.querySelector('th.MuiTableCell-head.RaDatagrid-expandHeader')
  }
  const dispatch = useDispatch()
  type ExpandInfo = {
    expanded: Identifier[];
    anySchemasExpanded: boolean;
  };
  const expandedInfo = useSelector<ReduxState, ExpandInfo>(store => {
    const s_expanded = (store.admin.resources[fieldSemanticRes]?.list.expanded ?? []) as Identifier[]
    return {
      expanded: s_expanded,
      anySchemasExpanded: (fieldsListCtrl.ids ?? []).some(id => s_expanded.includes(id)),
    }
  })
  const toggleExpandAll = async (e: MouseEvent) => {
    if (expandedInfo.anySchemasExpanded) {
      for (const id of (fieldsListCtrl.ids ?? []).filter(id => expandedInfo.expanded.includes(id))) {
        dispatch(toggleListItemExpand(fieldSemanticRes, id))
      }
    } else {
      const btn: HTMLButtonElement = e.target as any
      const th = btn.closest('th')
      for (const id of (fieldsListCtrl.ids ?? [])) {
        dispatch(toggleListItemExpand(fieldSemanticRes, id))
      }
      await waitMilis(20)
      th?.scrollIntoView({ block: 'start', behavior: 'instant' });
    }
  }
  return {
    ExpandAllPortal: () => {
      return (!th_ref.current ? null :
        createPortal(
          <ExpandRowButton classes={datagridClasses} 
            expanded={expandedInfo.anySchemasExpanded} 
            onClick={toggleExpandAll} 
          />,
          th_ref.current
        )
      )
    },

    setRef,
  }
}

const sorters = {
  id: (a: FieldSchemaResponseDto, b: FieldSchemaResponseDto) => (a.position ?? 0) < (b.position ?? 0) ? -1 : 1
} as any
